/**
 * delayed-navigation.js
 *
 * Purpose: Holds back links inside onsite-tracked elements with element target "notrackpage" for 1.5 s
 * and shows a loading state on the link meanwhile (TSFYDEV-538). These links lead to pages without our
 * onsite tracking (e.g. dm.de), so the external tag manager needs time to send the click before the
 * browser leaves the shop and cancels pending requests.
 *
 * Dependencies:
 * - snippets/onsite-tracking-attributes.liquid renders [data-delayed-navigation] (value: screen reader
 *   message) and the loading-state CSS (.delayed-navigation__spinner, [data-delayed-navigation-loading]).
 * - .visually-hidden from theme.css for the status message.
 * - Opts delayed links out of the page transition in theme.js via its "no-transition" class.
 * Loaded as module in layout/theme.liquid.
 */

const INT_NAVIGATION_DELAY_MS = 1500;
// Restores the link if the navigation gets cancelled (Esc, 204 response, download)
const INT_LOADING_RESET_MS = 5000;
const STR_ONSITE_TRACKING_SELECTOR = '[data-onsite-tracking]';
const STR_DELAY_ATTRIBUTE = 'data-delayed-navigation';
const STR_LOADING_ATTRIBUTE = 'data-delayed-navigation-loading';
const STR_STATUS_ATTRIBUTE = 'data-delayed-navigation-status';
const STR_SPINNER_CLASS = 'delayed-navigation__spinner';

/**
 * Checks whether a click must keep the browser default (new tab or window, download, modifier keys,
 * non-primary button, non-http link, or another script already handled it).
 * @param {MouseEvent} objEvent - Click event
 * @param {HTMLAnchorElement} elLink - Clicked link
 * @returns {boolean} True if the navigation must not be delayed
 */
function shouldKeepDefaultNavigation(objEvent, elLink) {
  const strTarget = (elLink.getAttribute('target') || '').toLowerCase();
  const boolHasModifierKey = objEvent.metaKey || objEvent.ctrlKey || objEvent.shiftKey || objEvent.altKey;
  const boolOpensElsewhere = strTarget !== '' && strTarget !== '_self';
  const boolIsWebLink = elLink.protocol === 'https:' || elLink.protocol === 'http:';

  return (
    objEvent.defaultPrevented ||
    objEvent.button !== 0 ||
    boolHasModifierKey ||
    boolOpensElsewhere ||
    elLink.hasAttribute('download') ||
    !boolIsWebLink
  );
}

/**
 * Returns the shared visually hidden live region for status messages, creating it on first use.
 * @returns {HTMLElement} Live region element
 */
function getStatusElement() {
  let elStatus = document.querySelector(`[${STR_STATUS_ATTRIBUTE}]`);

  if (!elStatus) {
    elStatus = document.createElement('span');
    elStatus.className = 'visually-hidden';
    elStatus.setAttribute('role', 'status');
    elStatus.setAttribute(STR_STATUS_ATTRIBUTE, '');
    document.body.append(elStatus);
  } // end if !elStatus

  return elStatus;
}

/**
 * Marks the link as busy for assistive technology and appends the spinner element styled by the snippet CSS.
 * @param {HTMLAnchorElement} elLink - Link that is being held back
 * @returns {void}
 */
function showLoadingState(elLink) {
  const elSpinner = document.createElement('span');
  elSpinner.className = STR_SPINNER_CLASS;
  elSpinner.setAttribute('aria-hidden', 'true');

  elLink.setAttribute(STR_LOADING_ATTRIBUTE, '');
  elLink.setAttribute('aria-busy', 'true');
  elLink.setAttribute('aria-disabled', 'true');
  elLink.append(elSpinner);
}

/**
 * Restores a link to its normal state.
 * @param {HTMLAnchorElement} elLink - Link in loading state
 * @returns {void}
 */
function resetLoadingState(elLink) {
  elLink.removeAttribute(STR_LOADING_ATTRIBUTE);
  elLink.removeAttribute('aria-busy');
  elLink.removeAttribute('aria-disabled');
  elLink.querySelector(`:scope > .${STR_SPINNER_CLASS}`)?.remove();
}

/**
 * Returns the innermost onsite-tracked element of a click target if it has delayed navigation.
 * The innermost one wins, so a nested element with another target is not delayed by its wrapper.
 * @param {EventTarget|null} elTarget - Click target
 * @returns {HTMLElement|null} Tracked element or null
 */
function getDelayedTrackedElement(elTarget) {
  if (!(elTarget instanceof Element)) return null;

  const elTracked = elTarget.closest(STR_ONSITE_TRACKING_SELECTOR);
  return elTracked?.hasAttribute(STR_DELAY_ATTRIBUTE) ? elTracked : null;
}

/**
 * Returns the link a click would follow if it belongs to an element with delayed navigation.
 * @param {EventTarget|null} elTarget - Click target
 * @returns {HTMLAnchorElement|null} Link or null
 */
function getDelayedLink(elTarget) {
  if (!getDelayedTrackedElement(elTarget)) return null;

  return elTarget.closest('a[href]');
}

/**
 * Opts the clicked link out of the page transition in theme.js. That handler is delegated to <body>,
 * prevents every link click and navigates after 200 ms, which would skip the delay. It skips links with
 * the class "no-transition", so the class is set in the capture phase before the handler checks it.
 * @param {MouseEvent} objEvent - Click event in the capture phase
 * @returns {void}
 */
function excludeFromPageTransition(objEvent) {
  getDelayedLink(objEvent.target)?.classList.add('no-transition');
}

/**
 * Delays links inside elements with element target "notrackpage". The event keeps bubbling,
 * only the default navigation is prevented, so the tag manager's click listener still fires.
 * @param {MouseEvent} objEvent - Click event delegated to the document
 * @returns {void}
 */
function handleDocumentClick(objEvent) {
  const elTracked = getDelayedTrackedElement(objEvent.target);
  const elLink = elTracked ? objEvent.target.closest('a[href]') : null;
  if (!elLink) return;

  // A second click during the delay must not start another navigation
  if (elLink.hasAttribute(STR_LOADING_ATTRIBUTE)) {
    objEvent.preventDefault();
    return;
  } // end if loading

  if (shouldKeepDefaultNavigation(objEvent, elLink)) return;

  objEvent.preventDefault();
  showLoadingState(elLink);
  getStatusElement().textContent = elTracked.dataset.delayedNavigation || '';

  window.setTimeout(() => navigateToLink(elLink), INT_NAVIGATION_DELAY_MS);
}

/**
 * Follows the link. If the page is still open after INT_LOADING_RESET_MS, the navigation was cancelled
 * and the link gets its normal state back; on a normal page change this timer never runs.
 * @param {HTMLAnchorElement} elLink - Link in loading state
 * @returns {void}
 */
function navigateToLink(elLink) {
  window.location.assign(elLink.href);
  window.setTimeout(() => resetLoadingState(elLink), INT_LOADING_RESET_MS);
}

/**
 * Resets all loading states when the page is restored from the back/forward cache,
 * otherwise the button would keep spinning after the user returns from the external page.
 * @param {PageTransitionEvent} objEvent - pageshow event
 * @returns {void}
 */
function handlePageShow(objEvent) {
  if (!objEvent.persisted) return;

  document.querySelectorAll(`[${STR_LOADING_ATTRIBUTE}]`).forEach(resetLoadingState);

  const elStatus = document.querySelector(`[${STR_STATUS_ATTRIBUTE}]`);
  if (elStatus) elStatus.textContent = '';
}

document.addEventListener('click', excludeFromPageTransition, true);
document.addEventListener('click', handleDocumentClick);
window.addEventListener('pageshow', handlePageShow);

// Create the live region up front, screen readers often ignore regions that are inserted and filled at once
if (document.querySelector(`[${STR_DELAY_ATTRIBUTE}]`)) {
  getStatusElement();
} // end if tracked elements exist
